import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

// ---------------------------------------------------------------
// /activate-node/success — Stripe checkout success landing page.
// Reads session_id + tier from the URL, optionally polls the
// backend to confirm payment_status, then shows a premium
// confirmation card in the same Summit aesthetic as the tier
// ladder page.
// ---------------------------------------------------------------

const GOLD = "#C9A84C";
const GREEN = "#22C55E";
const INK = "#0A0A0A";
const CARD = "#131313";
const HAIRLINE = "rgba(201,168,76,0.22)";
const MUTED = "#B7B7B0";
const IVORY = "#F5F1E3";
const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = `'Playfair Display', 'Cormorant Garamond', Georgia, serif`;
const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

const TIER_LABEL = {
  basic: "Basic Node · $97/mo",
  saturation: "Saturation Node · $297/mo",
  intelligence: "Intelligence Node · $497/mo",
};

export default function ActivateNodeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const tier = (params.get("tier") || "").toLowerCase();
  const [status, setStatus] = useState("verifying"); // verifying | paid | pending | unknown

  useEffect(() => {
    // Poll the status endpoint a handful of times so the customer
    // sees "Payment confirmed" without a hard refresh. Cap at ~8
    // attempts (24s) to avoid spinning forever if the webhook is
    // late — the page still reads as success regardless.
    if (!sessionId) {
      setStatus("unknown");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json().catch(() => ({}));
        if (data && data.payment_status === "paid") {
          if (!cancelled) setStatus("paid");
          return;
        }
      } catch (_) { /* transient, retry */ }
      if (attempts >= 8) {
        if (!cancelled) setStatus("pending");
        return;
      }
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const badge = status === "paid"
    ? { color: GREEN, text: "Payment Confirmed" }
    : status === "pending"
    ? { color: GOLD, text: "Payment Processing" }
    : status === "unknown"
    ? { color: GOLD, text: "Activation Received" }
    : { color: GOLD, text: "Verifying…" };

  return (
    <div
      data-testid="activate-node-success-root"
      style={{
        minHeight: "100vh",
        background: INK,
        color: IVORY,
        fontFamily: SANS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
      }}
    >
      <style>{`
        @keyframes ans-fade { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ans-pop { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.12); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .ans-fade { animation: ans-fade 900ms ease-out both; }
        .ans-check { animation: ans-pop 700ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      `}</style>

      <div
        className="ans-fade"
        style={{
          maxWidth: 640,
          width: "100%",
          background: CARD,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 18,
          padding: "48px 40px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 40px 90px -30px rgba(201,168,76,0.35)`,
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0, left: 0, right: 0, height: 1,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          }}
        />

        {/* Check mark medallion */}
        <div
          className="ans-check"
          data-testid="success-medallion"
          style={{
            margin: "0 auto 24px",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `linear-gradient(160deg, ${GOLD} 0%, #B4923D 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 20px 40px -12px rgba(201,168,76,0.55)`,
          }}
        >
          <span style={{ color: "#0A0A0A", fontSize: 44, fontWeight: 900, lineHeight: 1 }}>✓</span>
        </div>

        <div
          style={{
            display: "inline-block",
            padding: "6px 14px",
            border: `1px solid ${badge.color}`,
            color: badge.color,
            fontSize: 10,
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
            marginBottom: 20,
          }}
          data-testid="success-status-badge"
        >
          {badge.text}
        </div>

        <h1
          style={{
            fontFamily: SERIF,
            color: IVORY,
            fontSize: "clamp(2rem, 4.4vw, 2.8rem)",
            fontWeight: 700,
            margin: 0,
            lineHeight: 1.1,
            letterSpacing: "-0.005em",
          }}
          data-testid="success-headline"
        >
          Your Command Center is Live.
        </h1>
        <p
          style={{
            marginTop: 16,
            color: MUTED,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "1.08rem",
            lineHeight: 1.6,
            maxWidth: 480,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Amanda 4.0 will reach out within one business day to complete
          onboarding and kick off your Month 1 Release Phase.
        </p>

        {tier && TIER_LABEL[tier] && (
          <div
            data-testid="success-tier"
            style={{
              marginTop: 28,
              padding: "14px 18px",
              background: INK,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 10,
              color: GOLD,
              fontFamily: SANS,
              fontSize: "0.95rem",
              letterSpacing: "0.08em",
              fontWeight: 600,
            }}
          >
            Activated: {TIER_LABEL[tier]}
          </div>
        )}

        {sessionId && (
          <div
            data-testid="success-session-id"
            style={{
              marginTop: 16,
              color: MUTED,
              fontSize: 11,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Ref · {sessionId.slice(0, 20)}…
          </div>
        )}

        <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            to="/roundtable"
            data-testid="success-back-council"
            style={{
              padding: "13px 22px",
              background: "transparent",
              color: GOLD,
              border: `1px solid ${GOLD}`,
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontSize: "0.82rem",
            }}
          >
            Back to the Council
          </Link>
          <Link
            to="/"
            data-testid="success-home"
            style={{
              padding: "13px 22px",
              background: GOLD,
              color: "#0A0A0A",
              border: "none",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontSize: "0.82rem",
              boxShadow: `0 14px 30px -12px ${GOLD}`,
            }}
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}
